import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { FirefliesTranscriptSummary } from "../../api/generated";

function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
}

interface FirefliesTranscriptListProps {
  transcripts: FirefliesTranscriptSummary[];
  selectedIds: Set<string>;
  importedIds: Set<string>;
  onToggle: (id: string) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  hasMore: boolean;
  onLoadMore: () => void;
  isLoading: boolean;
}

export function FirefliesTranscriptList({
  transcripts,
  selectedIds,
  importedIds,
  onToggle,
  onSelectAll,
  onDeselectAll,
  hasMore,
  onLoadMore,
  isLoading,
}: FirefliesTranscriptListProps) {
  if (isLoading && transcripts.length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (transcripts.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <Typography sx={{
          color: "text.secondary"
        }}>No transcripts found.</Typography>
      </Box>
    );
  }

  const selectableCount = transcripts.filter(
    (t) => !importedIds.has(t.id),
  ).length;
  const allSelected =
    selectableCount > 0 && selectedIds.size === selectableCount;

  return (
    <Box>
      <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
        <Button
          size="small"
          onClick={allSelected ? onDeselectAll : onSelectAll}
        >
          {allSelected ? "Deselect All" : "Select All"}
        </Button>
        {selectedIds.size > 0 && (
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              alignSelf: "center"
            }}>
            {selectedIds.size} selected
          </Typography>
        )}
      </Stack>
      <List dense disablePadding>
        {transcripts.map((transcript) => {
          const isImported = importedIds.has(transcript.id);
          const isSelected = selectedIds.has(transcript.id);

          return (
            <ListItem key={transcript.id} disablePadding>
              <ListItemButton
                onClick={() => !isImported && onToggle(transcript.id)}
                disabled={isImported}
                dense
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Checkbox
                    edge="start"
                    checked={isSelected}
                    disabled={isImported}
                    tabIndex={-1}
                    disableRipple
                  />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Stack direction="row" spacing={1} sx={{
                      alignItems: "center"
                    }}>
                      <Typography variant="body2" noWrap>
                        {transcript.title}
                      </Typography>
                      {isImported && (
                        <Chip
                          label="Imported"
                          size="small"
                          color="info"
                          variant="outlined"
                        />
                      )}
                    </Stack>
                  }
                  secondary={
                    <Typography variant="caption" sx={{
                      color: "text.secondary"
                    }}>
                      {new Date(transcript.date).toLocaleDateString()} &middot;{" "}
                      {formatDuration(transcript.duration)} &middot;{" "}
                      {transcript.participant_count} participant
                      {transcript.participant_count !== 1 ? "s" : ""}
                    </Typography>
                  }
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
      {hasMore && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <Button onClick={onLoadMore} disabled={isLoading} size="small">
            {isLoading ? "Loading..." : "Load More"}
          </Button>
        </Box>
      )}
    </Box>
  );
}
